/* eslint-disable */

function AutoresponderPreviewPanel({ resendConfigured }) {
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const load = async () => {
    setLoading(true);
    try { setPreview(await settingsApi.autoresponderPreview()); }
    catch (e) { toast.error("Preview unavailable"); }
    finally { setLoading(false); }
  };
  const send = async () => {
    if (!window.confirm("Send the preview to your own admin email?")) return;
    setBusy(true);
    try {
      const r = await settingsApi.autoresponderTest();
      if (r.sent) toast.success("Preview email sent to your admin address");
      else if (r.reason === "rate_limited") toast.error(`Rate limited — retry in ${r.retry_after_s || 60}s`);
      else if (r.reason === "resend_not_configured") toast.error("Resend is not configured yet");
      else toast.error(`Send failed (${r.reason || "unknown"})`);
    } catch (e) { toast.error("Send failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-4 border border-line p-4" data-testid="autoresponder-preview-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="mono text-[10px] uppercase tracking-widest text-prestige">Preview & send-test</div>
        <div className="flex items-center gap-2">
          <button data-testid="btn-ar-preview" onClick={load} className="mono text-[10px] uppercase tracking-widest border border-line hover:border-navy text-navy px-3 py-1.5">Refresh preview</button>
          <button data-testid="btn-ar-send-test" onClick={send} disabled={!resendConfigured || busy}
                  className="bg-navy hover:bg-navy-hover text-white text-xs px-3 py-1.5 inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
            {busy ? "Sending…" : !resendConfigured ? "Send to my email (Resend not configured)" : "Send to my email"}
          </button>
        </div>
      </div>
      <div className="text-[10px] mono uppercase tracking-widest text-stone2 mb-2">
        Preview uses the exact same render path. It NEVER includes any submitted enquiry data.
      </div>
      {loading ? <div className="text-xs text-stone2">Loading preview…</div> :
       !preview ? <div className="text-xs text-stone2">Click "Refresh preview" to render.</div> :
       (<div>
          <div className="text-xs text-navy mb-2"><span className="mono text-[10px] uppercase tracking-widest text-prestige mr-2">Subject</span>{preview.subject}</div>
          <iframe data-testid="ar-preview-iframe" title="autoresponse-preview" srcDoc={preview.html}
                  className="w-full border border-line bg-white" style={{minHeight: 320}}/>
       </div>)}
    </div>
  );
}


import React, { useEffect, useState } from "react";
import { Breadcrumbs } from "../AdminLayout";
import { settingsApi, integrationsStatusApi } from "./api";
import { formatErr } from "../api";
import { toast, Toaster } from "sonner";
import { Save, Plus, Trash2, Info, ShieldCheck, Send } from "lucide-react";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const ROLES = ["super_administrator", "medical_reviewer", "medical_editor", "content_author", "seo_manager", "media_manager", "technical_administrator"];

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [status, setStatus] = useState({ recaptcha: {}, resend: {} });
  const [busy, setBusy] = useState(false);

  const reload = () => {
    settingsApi.get().then(setS).catch(e => toast.error(formatErr(e.response?.data?.detail)));
    integrationsStatusApi.status().then(setStatus).catch(() => {});
  };
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const patch = async (section, updates) => {
    setBusy(true);
    try { const r = await settingsApi.update({ [section]: updates }); setS(r); toast.success("Saved"); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!s) return <div className="p-6 text-xs text-stone2">Loading…</div>;

  const inputCls = "border border-line px-3 py-2 text-sm w-full";
  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="admin-settings">
      <Toaster position="top-right" richColors/>
      <Breadcrumbs items={[{ label: "Administration" }, { label: "Settings" }]}/>
      <div className="font-serif text-3xl text-navy mt-4 mb-6">Settings</div>

      {/* Site basics */}
      <Section title="Site" testid="settings-site">
        <Row label="Canonical base URL"><input data-testid="s-canonical" defaultValue={s.site.canonical_base_url || ""} onBlur={e => patch("site", { canonical_base_url: e.target.value })} placeholder="https://drtaniasingh.com" className={inputCls}/></Row>
        <Row label="Organisation name"><input data-testid="s-orgname" defaultValue={s.site.organization_name || ""} onBlur={e => patch("site", { organization_name: e.target.value })} className={inputCls}/></Row>
        <Row label="Default OG image URL"><input data-testid="s-og" defaultValue={s.site.default_og_image_url || ""} onBlur={e => patch("site", { default_og_image_url: e.target.value })} className={inputCls}/></Row>
        <Row label="Twitter / X handle"><input data-testid="s-tw" defaultValue={s.site.twitter_handle || ""} onBlur={e => patch("site", { twitter_handle: e.target.value })} placeholder="@drtaniasingh" className={inputCls}/></Row>
      </Section>

      {/* Enquiry notification */}
      <Section title="Enquiry notifications" testid="settings-enquiry">
        <div className="flex items-center gap-2 mb-3">
          <input data-testid="s-enq-enabled" type="checkbox" checked={!!s.enquiry_notification.enabled} onChange={e => patch("enquiry_notification", { enabled: e.target.checked })}/>
          <span className="text-sm text-navy">Send notification email when a new enquiry arrives</span>
        </div>
        <EmailListEditor testidPrefix="s-enq-rec" label="Recipients" list={s.enquiry_notification.recipients || []} onChange={v => patch("enquiry_notification", { recipients: v })}/>
        <EmailListEditor testidPrefix="s-enq-cc" label="Cc" list={s.enquiry_notification.cc_recipients || []} onChange={v => patch("enquiry_notification", { cc_recipients: v })}/>
      </Section>

      {/* reCAPTCHA policy */}
      <Section title="reCAPTCHA network-failure policy" testid="settings-recaptcha-policy">
        <Row label="On Google unreachable">
          <select data-testid="s-rc-policy" defaultValue={s.recaptcha_policy?.network_failure_policy || "fail_open"}
                  onChange={e => patch("recaptcha_policy", { network_failure_policy: e.target.value })} className={inputCls}>
            <option value="fail_open">Fail open — accept the enquiry (recommended for development)</option>
            <option value="fail_closed">Fail closed — reject (recommended for production)</option>
            <option value="quarantine">Quarantine — accept into spam-review queue</option>
          </select>
        </Row>
        <div className="mono text-[10px] uppercase tracking-widest text-stone2">
          Every skipped verification (network error) is operationally logged in the Audit Log as <code>recaptcha.network_error</code>. Internal error details are never disclosed to the public submitter.
        </div>
      </Section>

      {/* Autoresponder */}
      <Section title="Enquiry autoresponder (to submitter)" testid="settings-autoresponder">
        <div className="flex items-center gap-2 mb-3">
          <input data-testid="s-ar-enabled" type="checkbox" checked={!!s.enquiry_autoresponder?.enabled}
                 onChange={e => patch("enquiry_autoresponder", { enabled: e.target.checked })}/>
          <span className="text-sm text-navy">Send an automated confirmation to the submitter after an enquiry is accepted</span>
        </div>
        <Row label="Subject"><input data-testid="s-ar-subject" defaultValue={s.enquiry_autoresponder?.subject || ""} onBlur={e => patch("enquiry_autoresponder", { subject: e.target.value })} className={inputCls}/></Row>
        <Row label="Intro"><textarea data-testid="s-ar-intro" rows={2} defaultValue={s.enquiry_autoresponder?.body_intro || ""} onBlur={e => patch("enquiry_autoresponder", { body_intro: e.target.value })} className={inputCls}/></Row>
        <Row label="Safety disclaimer (mandatory)"><textarea data-testid="s-ar-disclaimer" rows={4} defaultValue={s.enquiry_autoresponder?.body_disclaimer || ""} onBlur={e => patch("enquiry_autoresponder", { body_disclaimer: e.target.value })} className={inputCls}/></Row>
        <Row label="Signature"><input data-testid="s-ar-signature" defaultValue={s.enquiry_autoresponder?.signature || ""} onBlur={e => patch("enquiry_autoresponder", { signature: e.target.value })} className={inputCls}/></Row>
        <Row label="Rate limit per email (hours)"><input data-testid="s-ar-rate" type="number" min={1} max={168} defaultValue={s.enquiry_autoresponder?.rate_limit_hours_per_email ?? 24} onBlur={e => patch("enquiry_autoresponder", { rate_limit_hours_per_email: Number(e.target.value) || 24 })} className={inputCls}/></Row>
        <div className="mono text-[10px] uppercase tracking-widest text-stone2">
          The autoresponse never echoes the submitter's message body or any medical details. It confirms receipt only and is not medical advice. Delivery outcomes are audit-logged as <code>enquiry.autoresponse</code> without provider ids or content.
        </div>
        <AutoresponderPreviewPanel resendConfigured={!!status.resend?.configured}/>
      </Section>

      {/* Weekly digest */}
      <Section title="Weekly Suggestions digest" testid="settings-digest">
        <div className="flex items-center gap-2 mb-3">
          <input data-testid="s-dig-enabled" type="checkbox" checked={!!s.weekly_digest.enabled} onChange={e => patch("weekly_digest", { enabled: e.target.checked })}/>
          <span className="text-sm text-navy">Send weekly digest email</span>
        </div>
        <Row label="Weekday">
          <select data-testid="s-dig-day" defaultValue={s.weekly_digest.weekday ?? 6} onChange={e => patch("weekly_digest", { weekday: Number(e.target.value) })} className={inputCls}>
            {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
          </select>
        </Row>
        <Row label="Time (local)">
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="s-dig-hour" type="number" min={0} max={23} defaultValue={s.weekly_digest.hour_local ?? 20} onBlur={e => patch("weekly_digest", { hour_local: Number(e.target.value) })} className={inputCls}/>
            <input data-testid="s-dig-minute" type="number" min={0} max={59} defaultValue={s.weekly_digest.minute_local ?? 0} onBlur={e => patch("weekly_digest", { minute_local: Number(e.target.value) })} className={inputCls}/>
          </div>
        </Row>
        <Row label="Timezone"><input data-testid="s-dig-tz" defaultValue={s.weekly_digest.timezone || "Asia/Kolkata"} onBlur={e => patch("weekly_digest", { timezone: e.target.value })} className={inputCls}/></Row>
        <Row label="Recipient roles">
          <div className="flex flex-wrap gap-2">
            {ROLES.map(r => {
              const on = (s.weekly_digest.recipient_roles || []).includes(r);
              return (
                <button key={r} data-testid={`s-dig-role-${r}`}
                        onClick={() => {
                          const next = on ? s.weekly_digest.recipient_roles.filter(x => x !== r) : [...(s.weekly_digest.recipient_roles || []), r];
                          patch("weekly_digest", { recipient_roles: next });
                        }}
                        className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border ${on ? "border-navy bg-navy text-white" : "border-line text-stone2 hover:border-navy"}`}>{r.replace(/_/g, " ")}</button>
              );
            })}
          </div>
        </Row>
        <EmailListEditor testidPrefix="s-dig-extra" label="Additional recipients" list={s.weekly_digest.extra_recipients || []} onChange={v => patch("weekly_digest", { extra_recipients: v })}/>
        <Row label="Skip if empty">
          <div className="flex items-center gap-2">
            <input data-testid="s-dig-skip" type="checkbox" checked={s.weekly_digest.skip_if_empty !== false} onChange={e => patch("weekly_digest", { skip_if_empty: e.target.checked })}/>
            <span className="text-xs text-stone2">Do not send when there are zero new pending suggestions</span>
          </div>
        </Row>
        {s.weekly_digest.last_sent_at && <div className="mono text-[10px] uppercase tracking-widest text-stone2 mt-2">Last sent: {s.weekly_digest.last_sent_at}</div>}
      </Section>

      {/* Integrations status */}
      <Section title="Third-party integrations" testid="settings-integrations">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <IntegrationCard title="Resend (Email)" configured={status.resend?.configured}
                           details={[
                             ["Sender", status.resend?.sender_email || "—"],
                             ["API key", status.resend?.api_key_hint ? `${status.resend.api_key_hint} (set)` : "not set"],
                           ]}/>
          <IntegrationCard title="Google reCAPTCHA v3" configured={status.recaptcha?.configured}
                           details={[
                             ["Site key", status.recaptcha?.site_key_public || "not set"],
                             ["Secret", status.recaptcha?.secret_key_hint ? `${status.recaptcha.secret_key_hint} (set)` : "not set"],
                             ["Score threshold", String(status.recaptcha?.score_threshold ?? "0.5")],
                           ]}/>
        </div>
        <div className="mt-4 border border-line p-4">
          <div className="mono text-[10px] uppercase tracking-widest text-prestige mb-2">Deliverability test</div>
          <div className="text-xs text-stone2 mb-3">
            Sends a clearly-marked test email to your configured enquiry recipients. Rate-limited to one call per 60 seconds. Available only when Resend is configured.
          </div>
          <TestEmailButton disabled={!status.resend?.configured}/>
        </div>
        <div className="mt-3 border border-line bg-clinical/40 px-3 py-2 text-xs text-stone2 flex items-start gap-2">
          <Info size={14} className="mt-0.5 text-prestige shrink-0"/>
          <div>Secrets are read from server environment variables only. They are never entered here, echoed in the UI, or included in exports. Set them through Emergent's secure environment manager and restart the backend.</div>
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div className="bg-white border border-line p-5 mb-5" data-testid={testid}>
      <div className="mono text-[10px] uppercase tracking-widest text-prestige mb-4">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Row({ label, children }) {
  return (
    <div className="grid grid-cols-12 gap-3 items-start">
      <div className="col-span-12 md:col-span-4 text-xs text-stone2 uppercase tracking-widest mono pt-1">{label}</div>
      <div className="col-span-12 md:col-span-8">{children}</div>
    </div>
  );
}
function EmailListEditor({ testidPrefix, label, list, onChange }) {
  const [draft, setDraft] = useState("");
  const add = () => { if (draft.includes("@")) { onChange([...(list || []), draft.trim()]); setDraft(""); } else toast.error("Enter a valid email"); };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <Row label={label}>
      <div className="space-y-2">
        <div className="flex flex-wrap gap-1">
          {list.map((e, i) => (
            <span key={i} data-testid={`${testidPrefix}-${i}`} className="inline-flex items-center gap-1 mono text-[10px] bg-clinical border border-line px-2 py-0.5">
              {e}
              <button onClick={() => remove(i)} className="text-[#9F1239] hover:text-[#7B0F2C] ml-1"><Trash2 size={10}/></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input data-testid={`${testidPrefix}-input`} value={draft} onChange={e => setDraft(e.target.value)} placeholder="email@example.com" className="border border-line px-3 py-1.5 text-sm flex-1"/>
          <button data-testid={`${testidPrefix}-add`} onClick={add} className="mono text-[10px] uppercase tracking-widest border border-line hover:border-navy text-navy px-3 py-1.5 inline-flex items-center gap-1"><Plus size={11}/> Add</button>
        </div>
      </div>
    </Row>
  );
}
function IntegrationCard({ title, configured, details }) {
  return (
    <div className="border border-line p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-navy font-medium">{title}</div>
        <span className={`mono text-[10px] uppercase tracking-widest px-2 py-0.5 inline-flex items-center gap-1 ${configured ? "bg-[#3F6250]/10 text-[#3F6250]" : "bg-[#D97706]/15 text-[#D97706]"}`}>
          {configured ? <><ShieldCheck size={10}/> configured</> : <>awaiting keys</>}
        </span>
      </div>
      <table className="w-full text-xs">
        <tbody>
          {details.map(([k, v]) => (
            <tr key={k} className="border-b border-line last:border-0"><td className="mono text-[10px] uppercase tracking-widest text-stone2 py-1">{k}</td><td className="py-1 text-navy">{v}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TestEmailButton({ disabled }) {
  const [busy, setBusy] = React.useState(false);
  const fire = async () => {
    if (!window.confirm("Send a deliverability test email to the configured recipients?")) return;
    setBusy(true);
    try {
      const r = await integrationsStatusApi.testEmail();
      if (r.sent) toast.success(`Test email sent to ${r.recipient_count} recipient${r.recipient_count !== 1 ? "s" : ""}`);
      else if (r.reason === "rate_limited") toast.error(`Rate limited — retry in ${r.retry_after_s || 60}s`);
      else if (r.reason === "resend_not_configured") toast.error("Resend is not configured yet");
      else if (r.reason === "no_recipients") toast.error("No recipients configured");
      else toast.error(`Send failed (${r.reason || "unknown"})`);
    } catch (e) { toast.error("Test failed"); }
    finally { setBusy(false); }
  };
  return (
    <button data-testid="btn-test-email" onClick={fire} disabled={disabled || busy}
            className="bg-navy hover:bg-navy-hover text-white text-sm px-4 py-2 inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
      <Send size={13}/> {busy ? "Sending…" : disabled ? "Send test email (Resend not configured)" : "Send test email"}
    </button>
  );
}
